import { useState, useEffect, useRef } from "react";
import { MapPin, X } from "lucide-react";
import { photonSuggest } from "@/utils/geocode";

/**
 * Free-text address input with optional Photon (OSM) autocomplete suggestions.
 *
 * - User is NEVER forced to pick a suggestion: typing is always accepted.
 * - When a suggestion is picked, `onPick({label, lat, lng})` is called so the
 *   parent can store precise coords. Manual typing still calls `onChange(text)`.
 * - Keyboard: ↑/↓ navigates, Enter selects highlighted suggestion, Esc closes.
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onPick,
  placeholder = "Adresse eingeben…",
  testId = "address-autocomplete",
  inputClassName = "",
  iconColor = "#0D9488",
  showClearButton = true,
  minChars = 3,
  debounceMs = 250,
  limit = 5,
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const suppressRef = useRef(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (suppressRef.current) { suppressRef.current = false; return; }
    const q = (value || "").trim();
    if (q.length < minChars) { setItems([]); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      const results = await photonSuggest(q, { limit, signal: ctrl.signal });
      setItems(results);
      setOpen(results.length > 0);
      setActive(-1);
    }, debounceMs);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [value, minChars, debounceMs, limit]);

  const pick = (s) => {
    suppressRef.current = true;
    onChange(s.label);
    if (onPick) onPick({ label: s.label, lat: s.lat, lng: s.lng });
    setOpen(false);
    setItems([]);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: iconColor }} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKey}
        onFocus={() => items.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
        className={`w-full pl-9 ${showClearButton && value ? "pr-9" : "pr-4"} py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${inputClassName}`}
      />
      {showClearButton && value && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { onChange(""); setItems([]); setOpen(false); }}
          data-testid={`${testId}-clear`}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
        >
          <X size={14} className="text-gray-400" />
        </button>
      )}
      {open && items.length > 0 && (
        <div
          data-testid={`${testId}-suggestions`}
          className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-[1000] overflow-hidden"
        >
          {items.map((s, i) => (
            <button
              key={`${s.lat},${s.lng},${i}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
              data-testid={`${testId}-suggestion-${i}`}
              className={`w-full text-left px-3 py-2 text-xs border-b border-gray-50 last:border-0 transition-colors ${i === active ? "bg-[#F0FDFA]" : "hover:bg-gray-50"}`}
            >
              <div className="flex items-start gap-2">
                <MapPin size={12} className="mt-0.5 text-gray-400 shrink-0" />
                <span className="text-gray-800 leading-snug">{s.label}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
